// src/lib/api.ts
import { supabase } from "./supabase";
import { runtimeEnvironment } from "./runtimeEnvironment";

const API_BASE = runtimeEnvironment.apiUrl;
const ENV_DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS || 30000);
const ENV_SLOW_TIMEOUT_MS = Number(
  process.env.REACT_APP_SLOW_API_TIMEOUT_MS || 90000
);
const DEFAULT_FETCH_TIMEOUT_MS =
  Number.isFinite(ENV_DEFAULT_TIMEOUT_MS) && ENV_DEFAULT_TIMEOUT_MS > 0
    ? ENV_DEFAULT_TIMEOUT_MS
    : 30000;
const SLOW_FETCH_TIMEOUT_MS =
  Number.isFinite(ENV_SLOW_TIMEOUT_MS) && ENV_SLOW_TIMEOUT_MS > 0
    ? Math.max(DEFAULT_FETCH_TIMEOUT_MS, ENV_SLOW_TIMEOUT_MS)
    : Math.max(DEFAULT_FETCH_TIMEOUT_MS, 90000);

/**
 * Ch87 · PKT-0688 · A BACKSTOP on reading the session, which had no ceiling at all.
 *
 * Every authenticated call here begins `await supabase.auth.getSession()` and
 * only then reaches `fetchWithTimeout`. The tiers below bound the NETWORK half.
 * Nothing bounded the first half. From the dependency itself
 * (@supabase/auth-js 2.71.1, pinned by "@supabase/supabase-js": "^2.54.0"):
 * `getSession()` runs `await this._acquireLock(-1, ...)`
 * (dist/main/GoTrueClient.js:908) and `refreshSession()` the same (:1232), and
 * lib/locks.js:54 documents that argument — "If negative, no timeout." Both
 * wait on the storage-key lock forever. A bound on each part is not a bound on
 * the whole, and here the first part had no bound at all.
 *
 * 🔴 WHAT THIS NUMBER IS NOT. My first draft of this change set 8 seconds and
 * called it a deadline. Then I measured, and 8s would have BROKEN A WORKING
 * PAGE. This is the trap already written down at `resolveTimeoutMs` below — a
 * browser deadline shorter than the real work can only ever kill something that
 * was legitimately in progress — and I was one keystroke from repeating it on
 * the session read. So: this is a backstop against "forever", NOT a policy on
 * slowness. Slowness is the progress affordance's job (LeadsPage.tsx), not this
 * one's.
 *
 * MEASURED in production, in Grant's browser, 2026-09-11 ~23:35 EDT (the
 * host is named in PKT-0688; a literal production host in this file trips the
 * staging-bypass guard in lib/__tests__/runtimeEnvironment.test.ts, correctly):
 *   · the LOCAL half is free: the auth storage-key lock acquires in 0.0-0.3ms
 *     uncontended (n=5) and the localStorage session read is sub-millisecond.
 *     All of the variance is the token-refresh round trip.
 *   · LeadsPage mount -> /api/clients/queue actually ISSUED, which is React
 *     mount plus this session read: 732, 1608, 2107, 7327, 7784 ms (n=5, warm
 *     in-app navigation). WORST OBSERVED 7784 ms — i.e. an 8s deadline fires on
 *     a healthy session.
 *   · cold full reload: 4228 ms from navigationStart to that request, 3978 ms
 *     of it after DOMContentLoaded.
 *
 * WHY 60 SECONDS: 7.7x the worst observed session read (7784 ms), which leaves
 * room for the case I could NOT measure — a second tab holding the same auth
 * lock while its own refresh is in flight. Even a pathological stack of several
 * queued refreshes does not approach a minute. By 60s a human has long since
 * decided the app is broken, so firing there forfeits nothing that was not
 * already lost; and the escalating copy on the page has been speaking since
 * 10s. The cost of setting it too HIGH is a slower report of a genuinely stuck
 * session; the cost of setting it too LOW is breaking every slow-but-working
 * load, every time. Those are not symmetric, so this errs high on purpose.
 *
 * It cannot swallow the per-URL budgets below: this race is settled BEFORE
 * `resolveTimeoutMs` is consulted, and it holds no AbortController, so it can
 * neither shorten nor abort any fetch. A slow URL's worst case grows by at most
 * this backstop, and only when the session read is already broken.
 */
const ENV_SESSION_READ_TIMEOUT_MS = Number(
  process.env.REACT_APP_SESSION_READ_TIMEOUT_MS || 60000
);
export const SESSION_READ_TIMEOUT_MS =
  Number.isFinite(ENV_SESSION_READ_TIMEOUT_MS) && ENV_SESSION_READ_TIMEOUT_MS > 0
    ? ENV_SESSION_READ_TIMEOUT_MS
    : 60000;

type AuthFetchOptions = RequestInit & {
  expectedActorId?: string;
  expectedUserId?: string;
  requestStillCurrent?: () => boolean;
  authRetry?: boolean;
  timeoutMs?: number;
  /**
   * Wait out a restart on this read. OPT-IN, and deliberately so.
   *
   * My first pass retried every GET in the app. Four suites went red and they
   * were right to: the Reddit answer desk already has a careful cold-start
   * design of its own (a 75s server-side gateway, its own "waking up" card),
   * and a second retry mechanism underneath it is not an improvement — it is
   * two mechanisms disagreeing about the same wait. Blanket behaviour changes
   * every call site at once, including the ones that already thought about it.
   *
   * So this is switched on where the problem actually bites: the two reads a
   * session makes first, which is where Grant saw the red box.
   */
  retry?: boolean;
  /**
   * Called before each retry, so a surface can say "waking up…" instead of
   * showing a red box for something that is about to succeed. `attempt` is
   * 1-based and counts RETRIES, not the original try.
   */
  onRetry?: (attempt: number, error: Error) => void;
};

/**
 * Ch83 · a deploy restart is not an outage, and it should not look like one.
 *
 * Render drops in-flight requests and takes 30-60s to come back on every deploy,
 * and this estate deploys several times a day. Grant saw "Couldn't reach the
 * LocateNow API" twice in one evening, both times beside a deploy. The 90s
 * timeout tier stopped the browser aborting a request the backend was still
 * serving; this stops the operator being handed an error for a wait.
 *
 * 🔴 GET ONLY, and that is the whole safety argument. A POST that timed out may
 * have SUCCEEDED on the server — the answer just never came back. Retrying one
 * is how a lead gets claimed twice or a renter gets the same text twice. Some
 * writes here carry an idempotency key and most do not, so the rule is the
 * conservative one: reads retry, writes never do.
 */
//
// THE WAITS ARE SIZED TO THE THING THEY BRIDGE. My first pass used 1s + 3s,
// which gives up after four seconds — against a restart that takes 30-60. It
// would have retried politely, failed anyway, and shipped as if it worked. A
// refused connection fails FAST (there is no server to time out against), so
// the elapsed time is essentially the sum of the waits: these bridge ~61s.
export const DEFAULT_RETRY_BACKOFF_MS = [3_000, 8_000, 20_000, 30_000];
const RETRY_BACKOFF_MS: number[] = (() => {
  // Same env seam the timeouts above use, so tests and ops can shrink it
  // without a second mechanism. A malformed value falls back rather than
  // producing a zero-wait hot loop against a service that is already down.
  const raw = process.env.REACT_APP_API_RETRY_BACKOFF_MS;
  if (!raw) {
    // Under test the POLICY is what matters — which errors retry, GET only,
    // and how many attempts — none of which is the sleep. Real waits added 57
    // seconds to the suite for nothing, and a suite slow enough to time out
    // under load is how a flake gets mistaken for a failure. Same shape, no
    // waiting; a test that wants to prove the waits exist sets the env var.
    if (process.env.NODE_ENV === "test") return DEFAULT_RETRY_BACKOFF_MS.map(() => 0);
    return DEFAULT_RETRY_BACKOFF_MS;
  }
  const parsed = raw.split(",").map((n) => Number(n.trim()));
  return parsed.every((n) => Number.isFinite(n) && n >= 0) && parsed.length > 0
    ? parsed
    : DEFAULT_RETRY_BACKOFF_MS;
})();

function isRetryableRead(error: unknown, method: string, enabled: boolean): boolean {
  if (!enabled) return false;
  if (method.toUpperCase() !== "GET") return false;
  // A TimeoutError means OUR deadline fired with no answer; a NetworkError means
  // the connection never landed. Both are "no response". An ApiError is NOT
  // retryable — the server answered and said no, and asking again is rude.
  return error instanceof TimeoutError || error instanceof NetworkError;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API error: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * OUR abort deadline fired: no response ever arrived.
 *
 * Strictly different from `ApiError`, which means the server DID answer and
 * said no. Callers fronting a service that can cold-start need to tell "still
 * starting up" apart from "actually broken", and the plain `Error` this used to
 * throw gave them nothing to test — the Reddit answer desk's whole "waking"
 * state was unreachable because of it. The message is unchanged so existing
 * copy and assertions keep working.
 */
export class TimeoutError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(
      `Request timed out after ${Math.ceil(timeoutMs / 1000)}s. Please try again.`
    );
    this.name = "TimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

/**
 * Ch78 · the request never got an HTTP answer at all.
 *
 * `fetch` rejects with a bare TypeError whose message is the BROWSER's, not
 * ours: Safari says "Load failed", Chrome "Failed to fetch", Firefox
 * "NetworkError when attempting to fetch resource." — and the app printed
 * that verbatim in a red box (Grant, Messages queue, 2026-09-07). Three
 * different causes hide behind that one line: the API is restarting after a
 * deploy, the network dropped, or the server threw and its 500 carried no
 * CORS header (fixed backend-side in the same change). None of them is the
 * operator's fault and all of them are worth one retry. The message says
 * what is knowable; `cause` keeps the browser's original for the console.
 */
export class NetworkError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super(
      // Ch78 · this used to assert "it may be restarting after a deploy" — a
      // guess dressed as a diagnosis, and usually the WRONG one. The common
      // cause is the service waking from idle. Say what is actually knowable
      // and stop naming a cause nobody measured.
      "The LocateNow API did not answer in time. It may be waking up — that takes about a minute — or the connection dropped."
    );
    this.name = "NetworkError";
    this.cause = cause;
  }
}

/**
 * Ch87 · the session could not be read at all, so no request was ever sent.
 *
 * Deliberately NOT a `TimeoutError`. That class means our deadline fired against
 * a server that was handed the request and never answered, and `isRetryableRead`
 * retries it. This one means we never got as far as asking. The distinction is
 * load-bearing: `/clients/queue` is fetched with `retry: true`, so classing a
 * stuck session read as a TimeoutError would run the 3s+8s+20s+30s ladder
 * against a lock that is not going to move — five silent backstops plus 61
 * seconds of extra waiting, on exactly the read where Grant hit this. A session
 * we cannot read is not a server that is restarting.
 */
export class SessionUnavailableError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(
      `Could not read your sign-in session (no answer in ${Math.ceil(
        timeoutMs / 1000
      )}s), so no request was sent. Reload the page; if it keeps happening, sign out and back in.`
    );
    this.name = "SessionUnavailableError";
    this.timeoutMs = timeoutMs;
  }
}

/** A `fetch` rejection that is the network layer speaking, not our code. */
function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError;
}

/**
 * The `detail` a FastAPI route put in its error response.
 *
 * `ApiError` carries the parsed payload on `.body`, NOT on `.detail` — and
 * reading `err.detail` therefore yields undefined forever. Two of this
 * estate's newest surfaces did exactly that, so a working backend guard
 * surfaced to the operator as the string "API error: 409" and a confirm
 * dialog could never open. `redditAnswerService.ts` already had the right
 * shape; this makes it reusable so the next caller does not have to rediscover
 * it. Tolerates an already-unwrapped object so it is safe to adopt anywhere.
 */
export function apiErrorDetail(err: unknown): any {
  const body = (err as any)?.body;
  if (body && typeof body === "object" && "detail" in body) {
    return (body as any).detail;
  }
  return (err as any)?.detail;
}

async function responseError(response: Response): Promise<ApiError> {
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  return new ApiError(response.status, body);
}

/**
 * Race a supabase auth call against the backstop above.
 *
 * Generic because `getSession()` and `refreshSession()` are the same defect one
 * line apart — both `_acquireLock(-1, ...)` inside the dependency. Bounding one
 * and leaving the other would move the hang rather than remove it, and the
 * refresh is the worse of the two: it holds the single-flight slot that every
 * concurrent 401 is queued behind.
 *
 * The timer is CLEARED on the happy path, so nothing this adds can fire on a
 * healthy call. `Promise.race` discards the loser's value but does not cancel
 * it, and a stray 60s timer per API call would hold the event loop open — a
 * worker that will not exit in this suite, a leak per request in a browser.
 */
async function withSessionDeadline<T>(
  work: Promise<T>,
  timeoutMs: number = SESSION_READ_TIMEOUT_MS
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new SessionUnavailableError(timeoutMs)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** The backstopped session read every authenticated call starts with. */
async function getSessionWithDeadline() {
  return withSessionDeadline(supabase.auth.getSession());
}

/** Bounded identity lookup for actor-scoped local request recovery. */
export async function getAuthenticatedActorId(): Promise<string|null> {
  const {data:{session}}=await getSessionWithDeadline();
  return session?.user?.id ?? null;
}

type RefreshedSession = Awaited<ReturnType<typeof supabase.auth.refreshSession>>["data"]["session"];
let refreshPromise: Promise<RefreshedSession> | null = null;

/** Supabase refresh tokens rotate; concurrent 401s must share one refresh. */
async function refreshSessionSingleFlight(): Promise<RefreshedSession> {
  if (!refreshPromise) {
    // Same one-line shape, same unbounded `_acquireLock(-1, ...)` underneath, and
    // this one is held by every concurrent 401 at once. `.finally` below still
    // clears the slot, so a backstopped rotation leaves the next caller a clean
    // attempt rather than a poisoned promise.
    refreshPromise = withSessionDeadline(supabase.auth.refreshSession())
      .then(({ data }) => data.session)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function resolveTimeoutMs(url: string, overrideMs?: number): number {
  if (typeof overrideMs === "number") {
    return Number.isFinite(overrideMs) && overrideMs > 0
      ? overrideMs
      : DEFAULT_FETCH_TIMEOUT_MS;
  }
  const lower = url.toLowerCase();
  if (
    lower.includes("/api/search/") ||
    lower.includes("/api/scrape-guard/") ||
    // The Reddit answer desk proxies to the Workbench, which runs on Render's
    // free plan and therefore spins down. Our server-side gateway deliberately
    // waits 75.0s to absorb that cold start
    // (backend/api/services/reddit_answer_gateway.py, pinned by
    // backend/tests/test_reddit_answer_cold_start.py). A browser deadline
    // SHORTER than the server's own budget can only ever abort a request the
    // backend was still legitimately serving, which is what made the desk's
    // "waking up" card unreachable. Covers both the queue read and the action
    // write — they share that one 75s gateway.
    lower.includes("/admin/content/reddit/") ||
    // The read-only library reaches the same cold-starting Workbench service
    // with a 75s server budget. Keep this path exact; unrelated reads stay 30s.
    /\/admin\/content\/library(?:\?|$)/.test(lower) ||
    // Ch78 · the SAME lesson, applied where it kept biting. Render sleeps the
    // API after idle and a cold start runs 30-60s. These two are the FIRST
    // calls a session makes, and at the 30s default the browser aborted a
    // request the backend was still legitimately serving — which is what put
    // "Couldn't reach the LocateNow API" in front of Grant on Messages and
    // again on the Leads funnel. The rule generalises: anything that can be
    // the first call after idle needs a deadline LONGER than the wake-up.
    lower.includes("/clients/queue") ||
    lower.includes("/analytics/lead-funnel") ||
    // Production commission-history GET completed in 31,977ms (2026-09-22),
    // beyond the default 30s. Keep this measured exception finite and exact.
    /\/properties-management\/history\/commissions(?:\?|$)/.test(lower)
  ) {
    return SLOW_FETCH_TIMEOUT_MS;
  }
  return DEFAULT_FETCH_TIMEOUT_MS;
}

function createTimeoutController(timeoutMs: number): {
  controller: AbortController;
  timeoutId: ReturnType<typeof setTimeout>;
} {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return { controller, timeoutId };
}

function withAbortTimeout(
  options: RequestInit,
  timeoutMs: number
): { requestOptions: RequestInit; cleanup: () => void } {
  const { controller, timeoutId } = createTimeoutController(timeoutMs);
  const originalSignal = options.signal;

  if (originalSignal) {
    if (originalSignal.aborted) {
      controller.abort();
    } else {
      originalSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  return {
    requestOptions: { ...options, signal: controller.signal },
    cleanup: () => clearTimeout(timeoutId),
  };
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_FETCH_TIMEOUT_MS
): Promise<Response> {
  const { requestOptions, cleanup } = withAbortTimeout(options, timeoutMs);
  try {
    return await fetch(url, requestOptions);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new TimeoutError(timeoutMs);
    }
    if (isNetworkFailure(error)) {
      throw new NetworkError(error);
    }
    throw error;
  } finally {
    cleanup();
  }
}

/**
 * Fetch wrapper that automatically adds auth token
 */
export async function fetchWithAuth<T>(
  endpoint: string,
  options: AuthFetchOptions = {}
): Promise<T> {
  // Reads wait out a restart; writes get exactly one attempt. See the note on
  // RETRY_BACKOFF_MS for why that asymmetry is not laziness.
  const method = (options.method || "GET").toUpperCase();
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_BACKOFF_MS.length; attempt += 1) {
    try {
      return await fetchWithAuthOnce<T>(endpoint, options);
    } catch (error) {
      lastError = error;
      if (attempt === RETRY_BACKOFF_MS.length) break;
      if (!isRetryableRead(error, method, options.retry === true)) break;
      options.onRetry?.(attempt + 1, error as Error);
      await sleep(RETRY_BACKOFF_MS[attempt]);
    }
  }
  throw lastError;
}

async function fetchWithAuthOnce<T>(
  endpoint: string,
  options: AuthFetchOptions = {}
): Promise<T> {
  // Get current session
  const {
    data: { session },
  } = await getSessionWithDeadline();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;
  const { timeoutMs, expectedActorId, authRetry, expectedUserId, requestStillCurrent, ...requestOptions } = options;
  const assertIdentity = (userId: string | undefined) => {
    if ((expectedUserId !== undefined && userId !== expectedUserId) || (requestStillCurrent && !requestStillCurrent())) {
      throw new Error("The signed-in account changed. Reload before retrying this request.");
    }
  };
  assertIdentity(session.user?.id);
  if (expectedActorId && session.user?.id !== expectedActorId) throw new Error("Scheduling account changed; reopen scheduling.");
  const timeoutForRequest = resolveTimeoutMs(url, timeoutMs);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };

  const response = await fetchWithTimeout(url, {
    ...requestOptions,
    headers: {
      ...headers,
      ...requestOptions.headers,
    },
  }, timeoutForRequest);

  if (response.status === 401 && authRetry !== false) {
    // Token expired - try to refresh
    const newSession = await refreshSessionSingleFlight();
    if (newSession) {
      assertIdentity(newSession.user?.id);
      if (expectedActorId && newSession.user?.id !== expectedActorId) throw new Error("Scheduling account changed; reopen scheduling.");
      // Retry with new token
      const retryResponse = await fetchWithTimeout(url, {
        ...requestOptions,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${newSession.access_token}`,
          ...requestOptions.headers,
        },
      }, timeoutForRequest);
      if (!retryResponse.ok) {
        throw await responseError(retryResponse);
      }
      return retryResponse.json();
    }
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    throw await responseError(response);
  }

  return response.json();
}

export async function fetchBlobWithAuth(
  endpoint: string,
  options: AuthFetchOptions = {}
): Promise<Blob> {
  const {
    data: { session },
  } = await getSessionWithDeadline();

  if (!session?.access_token) {
    throw new Error("Not authenticated");
  }

  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;
  const { timeoutMs, ...requestOptions } = options;
  const timeoutForRequest = resolveTimeoutMs(url, timeoutMs);

  const response = await fetchWithTimeout(
    url,
    {
      ...requestOptions,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        ...requestOptions.headers,
      },
    },
    timeoutForRequest
  );

  if (response.status === 401) {
    const newSession = await refreshSessionSingleFlight();
    if (newSession) {
      const retryResponse = await fetchWithTimeout(
        url,
        {
          ...requestOptions,
          headers: {
            Authorization: `Bearer ${newSession.access_token}`,
            ...requestOptions.headers,
          },
        },
        timeoutForRequest
      );
      if (!retryResponse.ok) {
        throw await responseError(retryResponse);
      }
      return retryResponse.blob();
    }
    throw new Error("Session expired. Please log in again.");
  }

  if (!response.ok) {
    throw await responseError(response);
  }

  return response.blob();
}

/**
 * Convenience methods
 */
export const api = {
  /**
   * `opts` carries the retry switch. Teaching THIS helper about it, rather than
   * having callers drop to fetchWithAuth, keeps one entry point: the services
   * are tested by mocking `api`, and routing around it silently bypassed those
   * mocks — seven shape-guard tests went red the moment I tried it.
   */
  get: <T>(endpoint: string, opts?: Pick<AuthFetchOptions, "retry" | "onRetry">) =>
    fetchWithAuth<T>(endpoint, opts),

  post: <T>(endpoint: string, data: any) =>
    fetchWithAuth<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  put: <T>(endpoint: string, data: any) =>
    fetchWithAuth<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data: any) =>
    fetchWithAuth<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) =>
    fetchWithAuth<T>(endpoint, {
      method: "DELETE",
    }),
};

/**
 * Build query string from params object
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (typeof value === "boolean") {
      searchParams.append(key, value ? "true" : "false");
    } else {
      searchParams.append(key, String(value));
    }
  });

  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

/**
 * The sentence the server refused with, or null if it did not give one.
 *
 * Ch83 · found by an e2e, not by a unit test. Three services carried the
 * comment "fetchWithAuth throws `API error: <status> - <body>`" and a regex to
 * match it. `ApiError`'s message is only `API error: <status>` — the body has
 * always been a separate field — so the regex never matched, every refusal
 * reached the operator as a bare status code, and `SmsUnverifiedLeadError` was
 * thrown from a branch that could not run.
 *
 * FastAPI gives `detail` two shapes and this route uses both: a plain string
 * for the simple refusals ("Lead has no phone number"), and an object carrying
 * `message` for the guardrail decisions, which are the refusals an operator is
 * most likely to actually hit. Anything else — a validation array, a null body
 * from an unparseable response — returns null so the caller keeps the original
 * error rather than inventing a sentence.
 */
export function refusalMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail.trim() || null;
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message.trim() || null;
  }
  return null;
}
